import React, { useMemo, useState } from "react";
import { ServerUrl } from "../../../store/api/http";

/**
 * Returns Israel timezone offset for a given date (DST-aware).
 * Israel: UTC+2 (winter), UTC+3 (summer, ~March 28 – Oct 26).
 */
function getIsraelOffset(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const march28 = new Date(y, 2, 28);
  const oct26 = new Date(y, 9, 26);
  const eventDate = new Date(y, m - 1, d);
  const isSummer = eventDate >= march28 && eventDate < oct26;
  return isSummer ? "+03:00" : "+02:00";
}

function pad(num) {
  return String(num).padStart(2, "0");
}

function escapeIcs(value) {
  return String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,");
}

function toUtcIcs(date) {
  return (
    date.getUTCFullYear() +
    pad(date.getUTCMonth() + 1) +
    pad(date.getUTCDate()) +
    "T" +
    pad(date.getUTCHours()) +
    pad(date.getUTCMinutes()) +
    pad(date.getUTCSeconds()) +
    "Z"
  );
}

function toIsraelLocalStamp(date) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value || "00";
  let hour = get("hour");
  if (hour === "24") hour = "00";
  return `${get("year")}${get("month")}${get("day")}T${hour}${get("minute")}${get("second")}`;
}

function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return (
    /iPad|iPhone|iPod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function buildEventFromData(data) {
  if (!data?.date || !data?.hour1) return null;

  const date = String(data.date).split("T")[0];
  const hourParts = String(data.hour1).split(":");
  const hour = (hourParts[0] || "00").padStart(2, "0");
  const minute = (hourParts[1] || "00").replace(/[^\d]/g, "").padStart(2, "0");
  const offset = getIsraelOffset(date);
  const start = new Date(`${date}T${hour}:${minute}:00${offset}`);
  if (Number.isNaN(start.getTime())) return null;

  const durationHours = data.eventType ? 5 : 3;
  const end = new Date(start.getTime() + durationHours * 60 * 60 * 1000);
  const title = data.eventType
    ? `${data.brideFN || ""} ו${data.groomFN || ""} - החתונה!`
    : data.calendarName
      ? data.calendarName
      : "אירוע";
  const description = `"${data.hall || ""}", ${data.waze || ""}`;
  const location = [data.address, data.city].filter(Boolean).join(", ");

  return { title, description, location, start, end };
}

function buildIcs(event) {
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//CallUp//Invitation//HE",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${event.start.getTime()}@callup.ltd`,
    `DTSTAMP:${toUtcIcs(new Date())}`,
    `DTSTART:${toUtcIcs(event.start)}`,
    `DTEND:${toUtcIcs(event.end)}`,
    `SUMMARY:${escapeIcs(event.title)}`,
    `DESCRIPTION:${escapeIcs(event.description)}`,
    `LOCATION:${escapeIcs(event.location)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

function buildGoogleUrl(event) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${toIsraelLocalStamp(event.start)}/${toIsraelLocalStamp(event.end)}`,
    details: event.description || "",
    location: event.location || "",
    ctz: "Asia/Jerusalem",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function downloadIcsBlob(ics, filename = "event.ics") {
  // iOS is picky: omit charset on the Blob type
  const blob = new Blob([ics], { type: "text/calendar" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  zIndex: 9999,
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  padding: "16px",
};

const sheetStyle = {
  width: "100%",
  maxWidth: "420px",
  background: "#fff",
  borderRadius: "20px",
  padding: "20px 18px 14px",
  boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
  direction: "rtl",
  textAlign: "center",
};

const optionStyle = {
  display: "block",
  width: "100%",
  border: "1px solid rgba(0,0,0,0.08)",
  borderRadius: "14px",
  padding: "14px 12px",
  marginTop: "10px",
  background: "#f7f8f9",
  color: "#222",
  fontWeight: 600,
  fontSize: "15px",
  textDecoration: "none",
  cursor: "pointer",
};

const Cal = (props) => {
  const [open, setOpen] = useState(false);
  const isEnglish = !!props.data?.isEnglish;
  const event = useMemo(() => buildEventFromData(props.data), [props.data]);
  const googleUrl = event ? buildGoogleUrl(event) : "#";
  const serverIcsUrl = props.token
    ? `${ServerUrl}users/calendar.ics?token=${encodeURIComponent(props.token)}`
    : null;
  // webcal works best for Apple Calendar on iOS when the file is hosted over https
  const appleUrl =
    serverIcsUrl && serverIcsUrl.startsWith("https://")
      ? serverIcsUrl.replace(/^https:/, "webcal:")
      : serverIcsUrl;

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!event) return;
    setOpen(true);
  };

  const handleAppleClick = (e) => {
    // Prefer hosted ICS / webcal. Fallback to local blob download.
    if (appleUrl) {
      setOpen(false);
      return;
    }
    e.preventDefault();
    downloadIcsBlob(buildIcs(event));
    setOpen(false);
  };

  const handleGoogleClick = () => {
    setOpen(false);
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleOpen(e);
        }}
        style={{ color: "inherit", textDecoration: "none", cursor: "pointer" }}
      >
        {props.div}
      </div>

      {open && event && (
        <div
          style={overlayStyle}
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            style={sheetStyle}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div style={{ fontWeight: 700, fontSize: "17px", marginBottom: 4 }}>
              {isEnglish ? "Add to calendar" : "הוספה ליומן"}
            </div>
            <div style={{ fontSize: "13px", color: "#666", marginBottom: 8 }}>
              {isEnglish
                ? "Choose your calendar app"
                : "בחרו את אפליקציית היומן שלכם"}
            </div>

            <a
              href={googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={optionStyle}
              onClick={handleGoogleClick}
            >
              {isEnglish ? "Google Calendar" : "Google Calendar"}
            </a>

            <a
              href={appleUrl || "#"}
              target={isIOSDevice() ? "_self" : "_blank"}
              rel="noopener noreferrer"
              style={optionStyle}
              onClick={handleAppleClick}
            >
              {isEnglish
                ? "Apple / Outlook (.ics)"
                : "Apple / Outlook (קובץ יומן)"}
            </a>

            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                ...optionStyle,
                background: "transparent",
                border: "none",
                color: "#888",
                fontWeight: 500,
              }}
            >
              {isEnglish ? "Cancel" : "ביטול"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Cal;
